import {
  AgentConversation,
  AgentConversationNote,
  AgentConversationResponse,
  AgentConversationsResponse,
  AgentSendMessageResponse,
  AgentMessage,
  AgentPrompt,
  AgentRun,
  AgentSettings,
  AgentTask,
  AiAgentConversationSortBy,
  AiAgentConversationSortDir,
  ArchiveConversationResponse,
  CheckResolvedResponse,
  DecideTaskResponse,
  DeletePromptResponse,
  IAiAgentPromptCreateBody,
  IAiAgentPromptUpdateBody,
  IAiAgentCreateNoteBody,
  IAiAgentSendMessageBody,
  IAiAgentTaskDecisionBody,
  IAiAgentToolApprovalBody,
  IUpdateAiAgentSettingsRequest,
  PromptResponse,
  PromptsResponse,
  ResolveConversationResponse,
  ToolApprovalResponse,
  ToolApprovalsResponse,
} from 'shared';

import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { CacheTags } from '../../types/cacheTags';
import { emptySplitApi } from '../baseApi';

export type AgentConversationSortBy = AiAgentConversationSortBy;

export type AgentConversationSortDir = AiAgentConversationSortDir;

export type {
  AgentConversation,
  AgentConversationNote,
  AgentMessage,
  AgentPrompt,
  AgentRun,
  AgentSettings,
  AgentTask,
};

type AgentSettingsResponse = {
  settings: AgentSettings;
};

export const aiAgentApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getAgentConversations: builder.query<AgentConversationsResponse, {
      organisationId: string;
      page?: number;
      pageSize?: number;
      sortBy?: AgentConversationSortBy;
      sortDir?: AgentConversationSortDir;
      statuses?: Array<AgentConversation[`status`] | `failed` | `archived`>;
    }>({
      query: ({ organisationId, page = 1, pageSize = 25, sortBy, sortDir, statuses }) => ({
        url: `ai-agent/conversations`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: {
          page,
          pageSize,
          sortBy,
          sortDir,
          statuses,
        },
      }),
      providesTags: result => result ?
        [
          ...result.conversations.map(agentConversation => ({ type: CacheTags.AgentConversations as const, id: agentConversation.id })),
          { type: CacheTags.AgentConversations, id: `LIST` },
        ] :
        [{ type: CacheTags.AgentConversations, id: `LIST` }],
    }),
    getAgentConversation: builder.query<AgentConversationResponse, { organisationId: string; conversationId: string }>({
      query: ({ organisationId, conversationId }) => ({
        url: `ai-agent/conversations/${conversationId}`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: (result, _error, args) => result ?
        [{ type: CacheTags.AgentConversations, id: result.conversation.id }] :
        [{ type: CacheTags.AgentConversations, id: args.conversationId }],
    }),
    createConversationNote: builder.mutation<AgentConversationNote, {
      organisationId: string;
      conversationId: string;
    } & IAiAgentCreateNoteBody>({
      query: ({ organisationId, conversationId, ...data }) => ({
        url: `ai-agent/conversations/${conversationId}/notes`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: (_result, _error, args) => ([
        { type: CacheTags.AgentConversations, id: `LIST` },
        { type: CacheTags.AgentConversations, id: args.conversationId },
      ]),
    }),
    sendConversationMessage: builder.mutation<AgentSendMessageResponse, {
      organisationId: string;
      conversationId: string;
    } & IAiAgentSendMessageBody>({
      query: ({ organisationId, conversationId, ...data }) => ({
        url: `ai-agent/conversations/${conversationId}/messages`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: (_result, _error, args) => ([
        { type: CacheTags.AgentConversations, id: `LIST` },
        { type: CacheTags.AgentConversations, id: args.conversationId },
      ]),
    }),
    decideTask: builder.mutation<DecideTaskResponse, {
      organisationId: string;
      conversationId: string;
      taskId: string;
    } & IAiAgentTaskDecisionBody>({
      query: ({ organisationId, conversationId, taskId, ...data }) => ({
        url: `ai-agent/conversations/${conversationId}/tasks/${taskId}/decide`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: (_result, _error, args) => ([
        { type: CacheTags.AgentConversations, id: `LIST` },
        { type: CacheTags.AgentConversations, id: args.conversationId },
      ]),
    }),
    resolveConversation: builder.mutation<ResolveConversationResponse, { organisationId: string; conversationId: string }>({
      query: ({ organisationId, conversationId }) => ({
        url: `ai-agent/conversations/${conversationId}/resolve`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (_result, _error, args) => ([
        { type: CacheTags.AgentConversations, id: `LIST` },
        { type: CacheTags.AgentConversations, id: args.conversationId },
      ]),
    }),
    archiveAgentConversation: builder.mutation<ArchiveConversationResponse, { organisationId: string; conversationId: string }>({
      query: ({ organisationId, conversationId }) => ({
        url: `ai-agent/conversations/${conversationId}/archive`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (_result, _error, args) => ([
        { type: CacheTags.AgentConversations, id: `LIST` },
        { type: CacheTags.AgentConversations, id: args.conversationId },
      ]),
    }),
    unarchiveAgentConversation: builder.mutation<ArchiveConversationResponse, { organisationId: string; conversationId: string }>({
      query: ({ organisationId, conversationId }) => ({
        url: `ai-agent/conversations/${conversationId}/unarchive`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (_result, _error, args) => ([
        { type: CacheTags.AgentConversations, id: `LIST` },
        { type: CacheTags.AgentConversations, id: args.conversationId },
      ]),
    }),
    checkResolved: builder.mutation<CheckResolvedResponse, { organisationId: string; conversationId: string }>({
      query: ({ organisationId, conversationId }) => ({
        url: `ai-agent/conversations/${conversationId}/check-resolved`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (_result, _error, args) => ([
        { type: CacheTags.AgentConversations, id: `LIST` },
        { type: CacheTags.AgentConversations, id: args.conversationId },
      ]),
    }),
    getToolApprovals: builder.query<ToolApprovalsResponse, { organisationId: string }>({
      query: ({ organisationId }) => ({
        url: `ai-agent/tool-approvals`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: result => result ?
        [
          ...result.preferences.map(preference => ({ type: CacheTags.AgentToolApprovals as const, id: preference.id })),
          { type: CacheTags.AgentToolApprovals, id: `LIST` },
        ] :
        [{ type: CacheTags.AgentToolApprovals, id: `LIST` }],
    }),
    setToolApproval: builder.mutation<ToolApprovalResponse, { organisationId: string } & IAiAgentToolApprovalBody>({
      query: ({ organisationId, ...data }) => ({
        url: `ai-agent/tool-approvals`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: [{ type: CacheTags.AgentToolApprovals, id: `LIST` }],
    }),
    getAgentSettings: builder.query<AgentSettings, { organisationId: string }>({
      query: ({ organisationId }) => ({
        url: `ai-agent/settings`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      transformResponse: (response: AgentSettings | AgentSettingsResponse) => {
        if (`settings` in response) {
          return response.settings;
        }

        return response;
      },
      providesTags: [{ type: CacheTags.AgentSettings }],
    }),
    updateAgentSettings: builder.mutation<AgentSettings, {
      organisationId: string;
    } & IUpdateAiAgentSettingsRequest>({
      query: ({ organisationId, ...data }) => ({
        url: `ai-agent/settings`,
        method: `PUT`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      transformResponse: (response: AgentSettings | AgentSettingsResponse) => {
        if (`settings` in response) {
          return response.settings;
        }

        return response;
      },
      invalidatesTags: [{ type: CacheTags.AgentSettings }],
    }),
    getAgentPrompts: builder.query<PromptsResponse, { organisationId: string }>({
      query: ({ organisationId }) => ({
        url: `ai-agent/prompts`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: result => result ?
        [
          ...result.prompts.map(prompt => ({ type: CacheTags.AgentPrompts as const, id: prompt.id })),
          { type: CacheTags.AgentPrompts, id: `LIST` },
        ] :
        [{ type: CacheTags.AgentPrompts, id: `LIST` }],
    }),
    createAgentPrompt: builder.mutation<PromptResponse, { organisationId: string } & IAiAgentPromptCreateBody>({
      query: ({ organisationId, ...data }) => ({
        url: `ai-agent/prompts`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: [{ type: CacheTags.AgentPrompts, id: `LIST` }],
    }),
    updateAgentPrompt: builder.mutation<PromptResponse, { organisationId: string; id: string } & IAiAgentPromptUpdateBody>({
      query: ({ organisationId, id, ...data }) => ({
        url: `ai-agent/prompts/${id}`,
        method: `PUT`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: (_result, _error, args) => ([
        { type: CacheTags.AgentPrompts, id: `LIST` },
        { type: CacheTags.AgentPrompts, id: args.id },
      ]),
    }),
    deleteAgentPrompt: builder.mutation<DeletePromptResponse, { organisationId: string; id: string }>({
      query: ({ organisationId, id }) => ({
        url: `ai-agent/prompts/${id}`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.AgentPrompts, id: `LIST` }],
    }),
  }),
});

export const {
  useGetAgentConversationsQuery,
  useGetAgentConversationQuery,
  useCreateConversationNoteMutation,
  useSendConversationMessageMutation,
  useDecideTaskMutation,
  useResolveConversationMutation,
  useArchiveAgentConversationMutation,
  useUnarchiveAgentConversationMutation,
  useCheckResolvedMutation,
  useGetToolApprovalsQuery,
  useSetToolApprovalMutation,
  useGetAgentSettingsQuery,
  useUpdateAgentSettingsMutation,
  useGetAgentPromptsQuery,
  useCreateAgentPromptMutation,
  useUpdateAgentPromptMutation,
  useDeleteAgentPromptMutation,
} = aiAgentApi;
